import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { campaignDeliveryState, verticalLabel, type CampaignWithRefs } from '@dmn/shared';
import { ChevronRight, Megaphone } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { CampaignStatusBadge } from '@/components/StatusBadge';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { cn, formatUtcDate, todayUtc } from '@/lib/utils';

export const Route = createFileRoute('/campaigns/')({ component: CampaignsIndex });

/**
 * Whether the campaign is actually delivering, in words an operator can act on.
 *
 * `status` is not the same question: a campaign reads `active` whether its
 * flight has ended, has not opened, or has no approved copy to show. Saying only
 * "Active" in a table is how someone concludes the platform is broken when it is
 * working exactly as configured.
 */
function deliveryNote(
  campaign: CampaignWithRefs,
  today: string
): { text: string; warn: boolean } | null {
  switch (campaignDeliveryState(campaign, today)) {
    case 'delivering':
      return null; // The green status badge already says it.
    case 'scheduled':
      return { text: `Starts ${formatUtcDate(campaign.flightStart, 'MMM D')}`, warn: false };
    case 'ended':
      return { text: `Flight ended ${formatUtcDate(campaign.flightEnd, 'MMM D')}`, warn: true };
    case 'no_creative':
      return { text: 'No approved creative', warn: true };
    case 'inactive':
      return null; // Draft / paused / archived — the badge is the whole story.
  }
}

function CampaignsIndex() {
  const campaigns = useQuery({ queryKey: queryKeys.campaigns, queryFn: api.campaigns.list });
  const today = todayUtc();

  const search = useTableSearch(campaigns.data, (c) => [
    c.name,
    c.id,
    c.sponsorName,
    c.status,
    ...c.verticals.map((vertical) => verticalLabel(vertical)),
  ]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader
        title="Campaigns"
        description="Flights, targeting and delivery state"
        actions={
          <Button asChild size="sm">
            <Link to="/campaigns/new">New campaign</Link>
          </Button>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search name, id, sponsor, status or specialty"
          label="Search campaigns"
          noun="campaign"
        />
        <Card className="shadow-none">
          <CardContent className="p-0">
            {campaigns.isError ? (
              <ErrorState error={campaigns.error} onRetry={() => void campaigns.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Campaign</TableHead>
                    <TableHead>Sponsor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Verticals</TableHead>
                    <TableHead>Placements</TableHead>
                    <TableHead>Flight</TableHead>
                    <TableHead className="text-right">Creatives</TableHead>
                    {/* Holds the chevron. Headed for screen readers only. */}
                    <TableHead className="w-8">
                      <span className="sr-only">Open</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {campaigns.isPending && <LoadingRows columns={8} />}
                  {pagination.pageRows.map((campaign) => {
                    const note = deliveryNote(campaign, today);

                    return (
                      /**
                       * The whole row is the link target.
                       *
                       * It is done with a stretched pseudo-element on the anchor
                       * rather than an `onClick` on the row, so this stays a real
                       * link: keyboard focus, middle-click and cmd-click to open
                       * in a new tab, and a sensible announcement to a screen
                       * reader all keep working. A row-level click handler throws
                       * all of that away for the same visible result.
                       */
                      <TableRow
                        key={campaign.id}
                        // `relative` makes the row the containing block for the
                        // stretched link below; `TableRow` already brings the
                        // hover tint and the transition.
                        className="group relative cursor-pointer focus-within:bg-muted/50"
                      >
                        <TableCell>
                          <Link
                            to="/campaigns/$campaignId"
                            params={{ campaignId: campaign.id }}
                            className="font-medium underline-offset-4 group-hover:underline after:absolute after:inset-0 after:content-['']"
                          >
                            {campaign.name}
                          </Link>
                          <p className="font-mono text-xs text-muted-foreground">{campaign.id}</p>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {campaign.sponsorName}
                        </TableCell>
                        <TableCell>
                          <CampaignStatusBadge status={campaign.status} />
                          {note && (
                            <p
                              className={cn(
                                'mt-1 text-xs',
                                note.warn
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-muted-foreground'
                              )}
                            >
                              {note.text}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {campaign.verticals.map((vertical) => (
                              <Badge key={vertical} variant="outline" className="font-normal">
                                {verticalLabel(vertical)}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {campaign.placementIds.length} placement
                          {campaign.placementIds.length === 1 ? '' : 's'}
                        </TableCell>
                        <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                          {formatUtcDate(campaign.flightStart, 'MMM D')} –{' '}
                          {formatUtcDate(campaign.flightEnd)}
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap tabular-nums">
                          <span
                            className={cn(
                              campaign.approvedCreativeCount === 0 && campaign.creativeCount > 0
                                ? 'text-amber-600 dark:text-amber-400'
                                : undefined
                            )}
                          >
                            {campaign.approvedCreativeCount}/{campaign.creativeCount}
                          </span>
                          <span className="ml-1 text-xs text-muted-foreground">approved</span>
                        </TableCell>
                        <TableCell className="w-8 text-right">
                          <ChevronRight
                            aria-hidden
                            className="ml-auto size-4 text-muted-foreground/40 transition-colors group-hover:text-foreground"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="campaigns" />
            {campaigns.data?.length === 0 && (
              <EmptyState
                title="No campaigns"
                description="A campaign needs a sponsor and at least one placement to target."
                icon={Megaphone}
                action={
                  <Button asChild size="sm">
                    <Link to="/campaigns/new">New campaign</Link>
                  </Button>
                }
              />
            )}
          </CardContent>
        </Card>
      </PageBody>
    </>
  );
}
